/**
 * Global search in the top bar (04 §0, HANDOFF §0.2). Only products are searchable today (`GET /products?q=`);
 * a typed case id (`AEV-2026-0007`) is routed to the cases list filter. Versions, CVEs and components in the same
 * box are "needs backend". ⌘K / Ctrl+K focuses the box from anywhere.
 *
 * ARIA combobox (list autocomplete): arrow keys move the active option, Enter opens it, Escape closes the list.
 */
import { lazy, Suspense, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useListProducts } from "@brevalt/api-client/customer";
import { useCan } from "@brevalt/app-core";

/** A case's human id: prefix, year, number (`AEV-2026-0007`, `SI-2026-0001`, `BRV-DRILL-0001`). */
const CASE_ID = /^[A-Z]{2,5}-(?:\d{4}|DRILL)-\d{1,6}$/i;
const DEBOUNCE_MS = 250;
const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [value, ms]);
  return debounced;
}

/** The result list loads when the user first types (not in the initial bundle). */
const SearchResults = lazy(() => import("./search-results"));

export interface Option {
  key: string;
  label: string;
  to: string;
  kind: "case" | "product";
}

export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const can = useCan();
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const q = useDebounced(query.trim(), DEBOUNCE_MS);
  const products = useListProducts(
    { q, limit: 8 },
    { query: { enabled: open && q.length >= 2 && can("product.read"), staleTime: 30_000 } },
  );

  // ⌘K / Ctrl+K from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const trimmed = query.trim();
  const caseOptionFor = (text: string): Option | null => {
    if (!CASE_ID.test(text) || !can("case.read")) return null;
    const caseId = text.toUpperCase();
    return {
      key: `case:${caseId}`,
      label: t("shell.find_case_caseid", { caseId }),
      to: `/cases?search=${encodeURIComponent(caseId)}`,
      kind: "case",
    };
  };
  const options: Option[] = [];
  const caseOption = caseOptionFor(trimmed);
  if (caseOption) options.push(caseOption);
  if (q.length >= 2 && q === trimmed) {
    for (const p of products.data?.items ?? []) {
      if (p.id && p.name) options.push({ key: p.id, label: p.name, to: `/products/${p.id}`, kind: "product" });
    }
  }
  const searching = q !== trimmed || (products.isFetching && options.length === 0);
  const showList = open && trimmed.length >= 2;
  const current = options[Math.min(active, options.length - 1)];

  // An Enter pressed before the results for the typed text arrived: act once they are in.
  const pendingEnter = useRef<string | null>(null);
  const settled = q === trimmed && !products.isFetching;
  const first = options[0];
  const chooseRef = useRef<(o: Option | undefined) => void>(() => undefined);
  useEffect(() => {
    if (pendingEnter.current === null || pendingEnter.current !== trimmed || !settled) return;
    chooseRef.current(first);
  }, [settled, trimmed, first]);

  const choose = (o: Option | undefined) => {
    pendingEnter.current = null;
    if (!o) return;
    setOpen(false);
    setQuery("");
    input.current?.blur();
    void navigate({ to: o.to });
  };

  useLayoutEffect(() => {
    chooseRef.current = choose;
  });

  return (
    <div className="relative">
      <label className="relative block">
        <span className="sr-only">{t("shell.search_products_versions_cve_components_or")}</span>
        <Search aria-hidden className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && current ? `${listId}-${current.key}` : undefined}
          value={query}
          placeholder={t("shell.search_products_cve_case_id")}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={() => {
            // A pending Enter belongs to this visit of the box: leaving drops it (no late navigation).
            pendingEnter.current = null;
            // Let a click on an option land first.
            setTimeout(() => {
              // Focused again meanwhile (the user came back to the box): keep the list.
              if (document.activeElement !== input.current) setOpen(false);
            }, 150);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, Math.max(options.length - 1, 0)));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              // Enter acts on what is typed now, never on results of an earlier query: a case id goes to the
              // cases filter at once; otherwise wait until the results for this text are in.
              const typed = e.currentTarget.value.trim();
              const caseOption = caseOptionFor(typed);
              if (caseOption) choose(caseOption);
              else if (typed !== q || products.isFetching) pendingEnter.current = typed;
              else choose(current);
            } else if (e.key === "Escape") {
              pendingEnter.current = null;
              setOpen(false);
            }
          }}
          className="h-9 w-full rounded-input border border-border-input bg-surface pl-9 pr-12 text-sm placeholder:text-text-muted"
        />
        <kbd
          aria-hidden
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xs border border-border px-1.5 font-mono text-2xs text-text-muted"
        >
          {isMac() ? t("shell.search_shortcut_mac") : t("shell.search_shortcut_other")}
        </kbd>
      </label>
      {showList && (
        <Suspense fallback={null}>
          <SearchResults
            listId={listId}
            options={options}
            current={current}
            status={
              options.length === 0
                ? searching
                  ? t("shell.searching")
                  : t("shell.no_products_match_query", { query: trimmed })
                : null
            }
            onChoose={choose}
            onHover={setActive}
          />
        </Suspense>
      )}
    </div>
  );
}
